<template>
  <div class="color-dropdown" ref="dropdownRef">
    <div class="selected" @click="toggleDropdown">
      <div v-if="selectedColors.length" class="palette-colors">
        <span v-for="(c, i) in selectedColors" :key="i" class="color-box" :style="{ backgroundColor: c }"></span>
      </div>
      <span class="palette-name palette-name-label" :title="selectedLabel">
        {{ selectedLabel }}
      </span>
      <span class="arrow" :class="{ open: open }">></span>
    </div>

    <ul v-if="open" class="dropdown-list" @click.stop>
      <!-- 手动输入 -->
      <li class="dropdown-item custom-input-item">
        <input v-model="manualInput" @keydown.enter.prevent="applyCustomColors" placeholder="输入颜色，逗号分隔"
          class="manual-input" @click.stop />
        <button class="apply-btn" @click.stop="applyCustomColors">应用</button>
      </li>

      <li v-if="manualError" class="dropdown-item error-item">{{ manualError }}</li>

      <!-- 预设色板 -->
      <li v-for="(colors, name) in colorPalettes" :key="name" @click="selectPalette(name)" class="dropdown-item">
        <div class="palette-colors">
          <span v-for="(c, i) in colors" :key="i" class="color-box" :style="{ backgroundColor: c }"></span>
        </div>
        <span class="palette-name palette-name-label" :title="name">{{ name }}</span>
      </li>
    </ul>
  </div>
</template>

<script setup>
  import {
    ref,
    watch,
    onMounted,
    onBeforeUnmount,
    computed,
    defineProps,
    defineEmits,
  } from "vue";
  import { storeToRefs } from "pinia";
  // import { useColorStore } from "@/store/handleColor.js";
  import { useStore } from "@/Store/index.js";

  // const colorStore = useColorStore();
  const store = useStore();
  const { all_colors: colorPalettes } = storeToRefs(store);

  const props = defineProps({
    modelValue: {
      type: String,
      default: "",
    },
  });
  const emit = defineEmits(["update:modelValue", "change"]);

  const selectedKey = ref(null);
  const selectedColors = ref([]);
  const isCustom = ref(false);
  const open = ref(false);
  const dropdownRef = ref(null);

  const manualInput = ref("");
  const manualError = ref("");

  const selectedLabel = computed(() => {
    if (isCustom.value) return "自定义";
    return selectedKey.value ? selectedKey.value : "请选择颜色";
  });

  function toggleDropdown() {
    open.value = !open.value;
  }

  function selectPalette(name) {
    selectedKey.value = name;
    isCustom.value = false;
    selectedColors.value = colorPalettes.value[name] || [];

    // 🔥 同步到输入框
    manualInput.value = selectedColors.value.join(",");
    manualError.value = "";

    const value = manualInput.value;
    emit("update:modelValue", value);
    emit("change", value);
    open.value = false;
  }

  function isValidColor(str) {
    if (!str) return false;
    const s = new Option().style;
    s.color = "";
    s.color = str;
    return s.color !== "";
  }

  function applyCustomColors() {
    manualError.value = "";
    const arr = manualInput.value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!arr.length) {
      manualError.value = "请输入至少一个颜色";
      return;
    }

    const invalid = arr.filter((c) => !isValidColor(c));
    if (invalid.length) {
      manualError.value = `无法识别: ${invalid.join(", ")}`;
      return;
    }

    selectedKey.value = null;
    selectedColors.value = arr;
    isCustom.value = true;

    const value = manualInput.value;
    emit("update:modelValue", value);
    emit("change", value);
    open.value = false;
  }
  // 点击外部dom关闭颜色下拉框
  function handleClickOutside(e) {
    if (dropdownRef.value && !dropdownRef.value.contains(e.target)) {
      open.value = false;
    }
  }

  watch(() => props.modelValue, (newVal) => {
    if (!newVal) {
      selectedKey.value = null;
      selectedColors.value = [];
      isCustom.value = false;
      manualInput.value = "";
      manualError.value = "";
      return;
    }
    const entry = Object.entries(colorPalettes.value || {}).find(
      ([, colors]) => colors.join(",") === newVal
    );
    if (entry) {
      selectedKey.value = entry[0];
      selectedColors.value = entry[1];
      isCustom.value = false;

      // 同步到输入框
      manualInput.value = selectedColors.value.join(",");
      manualError.value = "";
    } else {
      selectedKey.value = null;
      selectedColors.value = newVal.split(",").map((s) => s.trim()).filter(Boolean);
      isCustom.value = true;

      // 同步到输入框
      manualInput.value = newVal;
      manualError.value = "";
    }
  },
    { immediate: true }
  );

  onMounted(() => {
    document.addEventListener("click", handleClickOutside);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("click", handleClickOutside);
  });
</script>

<style scoped>
  .color-dropdown {
    position: relative;
    width: 100%;
    font-size: 14px;
  }

  .selected {
    height: 32px;
    border: 1px solid #ccc;
    border-radius: 6px;
    /* padding: 6px 10px; */
    padding: 0 3px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    cursor: pointer;
    background: #fff;
    gap: 6px;
  }

  .arrow {
    margin-left: auto;
    font-size: 14px;
    color: #666;
    display: inline-block;
    transition: transform 0.2s ease;
  }

  .arrow.open {
    transform: rotate(90deg);
  }

  .dropdown-list {
    position: absolute;
    top: 100%;
    left: 0;

    width: 100%;
    border: 1px solid #ccc;
    background: #fff;
    border-radius: 6px;
    margin-top: 4px;
    list-style: none;
    padding: 0;
    max-height: 220px;
    overflow-y: auto;
    z-index: 1000;
  }

  .dropdown-item {
    padding: 6px 10px;
    display: flex;
    justify-content: space-between;
    cursor: pointer;
    align-items: center;
  }

  .dropdown-item:hover {
    background: #f5f5f5;
  }

  .palette-colors {
    display: flex;
    width: 100%;
    overflow: hidden;
  }

  .color-box {
    flex: 1;
    height: 25px;
    border: none;
    border-radius: 0;
    /*  margin-right: 4px; */
    box-sizing: border-box;
  }

  .palette-name-label {
    display: inline-block;
    width: 80px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* 手动输入相关样式 */
  .custom-input-item {
    gap: 8px;
    cursor: default;
  }

  .manual-input {
    flex: 1;
    padding: 6px 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    outline: none;
    min-width: 0;
  }

  .apply-btn {
    margin-left: 8px;
    padding: 6px 10px;
    border: 1px solid #ccc;
    background: #fff;
    border-radius: 4px;
    cursor: pointer;
  }

  .apply-btn:hover {
    background: #f0f0f0;
  }

  .error-item {
    color: #d9534f;
    font-size: 12px;
    padding: 4px 10px;
  }
</style>